import React, { useState } from 'react';
import { Plus, X, CheckCircle2 } from 'lucide-react';

const DEFAULT_ROLES = [
  { id: 'daglig_leder', name: 'Daglig leder' },
  { id: 'hms_ansvarlig', name: 'HMS-ansvarlig' },
  { id: 'verneombud', name: 'Verneombud' },
  { id: 'brannvernleder', name: 'Brannvernleder' },
  { id: 'kvalitetssjef', name: 'Kvalitetssjef' },
  { id: 'miljoansvarlig', name: 'Miljøansvarlig' },
  { id: 'prosjektleder', name: 'Prosjektleder' },
  { id: 'lageransvarlig', name: 'Lageransvarlig' },
  { id: 'driftsleder', name: 'Driftsleder' },
  { id: 'personalansvarlig', name: 'Personalansvarlig' },
];

const INITIALLY_SELECTED = ['daglig_leder', 'hms_ansvarlig'];

export default function RolePicker({ selectedRoles, onRolesChange, variant = 'dark' }) {
  const [customRoleName, setCustomRoleName] = useState('');
  const [allRoles, setAllRoles] = useState(DEFAULT_ROLES);

  const isDark = variant === 'dark';

  const toggleRole = (id) => {
    if (selectedRoles.includes(id)) {
      onRolesChange(selectedRoles.filter(r => r !== id));
    } else {
      onRolesChange([...selectedRoles, id]);
    }
  };

  const addCustomRole = () => {
    const name = customRoleName.trim();
    if (!name) return;
    const id = `custom_${name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-zæøå0-9_]/g, '')}`;
    if (allRoles.some(r => r.id === id || r.name.toLowerCase() === name.toLowerCase())) {
      // Already exists — just select it
      if (!selectedRoles.includes(id)) {
        onRolesChange([...selectedRoles, id]);
      }
      setCustomRoleName('');
      return;
    }
    setAllRoles(prev => [...prev, { id, name, custom: true }]);
    onRolesChange([...selectedRoles, id]);
    setCustomRoleName('');
  };

  const removeCustomRole = (id) => {
    setAllRoles(prev => prev.filter(r => r.id !== id));
    onRolesChange(selectedRoles.filter(r => r !== id));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomRole();
    }
  };

  return (
    <div className="space-y-3" data-testid="role-picker">
      {/* Role list */}
      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {allRoles.map(role => (
          <div
            key={role.id}
            className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
              selectedRoles.includes(role.id)
                ? isDark ? 'border-cyan-500 bg-cyan-500/10' : 'border-blue-500 bg-blue-50'
                : isDark ? 'border-slate-700 hover:border-slate-600' : 'border-gray-200 hover:border-blue-300'
            }`}
            onClick={() => toggleRole(role.id)}
            data-testid={`role-${role.id}`}
          >
            <div className="flex items-center gap-3">
              <span className={isDark ? 'text-white text-sm' : 'text-gray-900 text-sm'}>{role.name}</span>
              {role.custom && (
                <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-200 text-gray-500'}`}>Egendefinert</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedRoles.includes(role.id) && (
                <CheckCircle2 className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-blue-500'}`} />
              )}
              {role.custom && (
                <button
                  onClick={(e) => { e.stopPropagation(); removeCustomRole(role.id); }}
                  className={`p-1 rounded-lg hover:bg-red-500/20 ${isDark ? 'text-slate-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'}`}
                  data-testid={`remove-role-${role.id}`}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add custom role */}
      <div className={`flex gap-2 pt-2 border-t ${isDark ? 'border-slate-800' : 'border-gray-200'}`}>
        <input
          type="text"
          value={customRoleName}
          onChange={e => setCustomRoleName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Legg til egen rolle..."
          className={`flex-1 px-3 py-2 rounded-xl text-sm border ${
            isDark
              ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500'
              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
          }`}
          data-testid="custom-role-input"
        />
        <button
          onClick={addCustomRole}
          disabled={!customRoleName.trim()}
          className={`px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-1.5 disabled:opacity-40 transition-all ${
            isDark
              ? 'bg-cyan-600 text-white hover:bg-cyan-500'
              : 'bg-blue-600 text-white hover:bg-blue-500'
          }`}
          data-testid="add-custom-role-btn"
        >
          <Plus className="w-4 h-4" /> Legg til
        </button>
      </div>

      {/* Counter */}
      <div className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
        {selectedRoles.length} rolle{selectedRoles.length !== 1 ? 'r' : ''} valgt
      </div>
    </div>
  );
}

export { DEFAULT_ROLES, INITIALLY_SELECTED };
